import { useAuth } from '@src/data/settings';
import { trpcReact } from '@src/data/trpc';
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useEffectiveAccount } from './useEffectiveAccount';

export const useReferralRewards = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getReferralRewards.useQuery(
    {
      address: effectiveAddress!,
      auth,
      challengeAuth,
    },
    {
      enabled: !!effectiveAddress && (!!auth || !!challengeAuth),
    }
  );
};

export const useReferralCode = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getReferralCode.useQuery(
    {
      auth,
      address: effectiveAddress!,
      challengeAuth,
    },
    {
      enabled: !!effectiveAddress && (!!auth || !!challengeAuth),
    }
  );
};

export const useUpdateReferralCode = (newReferralCode?: string) => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);
  const trpcReactUtils = trpcReact.useUtils();

  const { mutate, isLoading, isError, error, isSuccess } =
    trpcReact.updateReferralCode.useMutation({
      onSuccess: () => {
        toast.success('Updated referral code!');
        trpcReactUtils.getReferralCode.invalidate({
          auth,
          address: effectiveAddress!,
          challengeAuth,
        });
      },
      onError: (e) => {
        toast.error(`Couldn't update referral code: ${e.message}`);
      },
    });

  const updateReferralCode = useCallback(() => {
    let errorMsg;
    if (!newReferralCode) {
      errorMsg = 'No new referral code entered!';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    if (!/^[a-zA-Z0-9_]{3,15}$/.test(newReferralCode)) {
      errorMsg =
        'Referral code must be 3-15 characters long and contain only letters, numbers, and underscores';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
    if (!effectiveAddress) {
      errorMsg = 'Not connected';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    } else if (!(!!auth || !!challengeAuth)) {
      errorMsg = 'No auth found';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }

    mutate({
      address: effectiveAddress,
      newReferralCode,
      auth,
      challengeAuth,
    });
  }, [newReferralCode]);

  return {
    updateReferralCode,
    isError,
    error,
    isLoading,
    isSuccess,
  };
};

export const useReferralRewardQuest = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getReferralRewardQuest.useQuery(
    {
      auth,
      challengeAuth,
      address: effectiveAddress!,
    },
    {
      enabled: !!effectiveAddress && (!!auth || !!challengeAuth),
    }
  );
};

export const useClaimReferralReward = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);
  const trpcReactUtils = trpcReact.useUtils();

  const { mutate, isLoading, isError, error, isSuccess } =
    trpcReact.claimReferralReward.useMutation({
      onSuccess: () => {
        toast.success('Claimed Reward! Your reward will arrive shortly.');
        trpcReactUtils.getReferralRewardQuest.invalidate({
          auth,
          challengeAuth,
          address: effectiveAddress!,
        });
      },
      onError: (e) => {
        const message = `Couldn't claim rewards: ${e.message}`;
        toast.error(message, {
          duration: 7_500,
        });
        console.error(message, e);
      },
    });

  const claimReward = () => {
    if (!effectiveAddress || !(!!auth || !!challengeAuth)) {
      console.error('No account found on claimReward');
      throw new Error('No Account found!');
    }

    if (!(!!auth || !!challengeAuth)) {
      console.error('No auth found on claimReward');
      throw new Error('No Auth found!');
    }

    mutate({
      auth,
      challengeAuth,
      address: effectiveAddress,
    });
  };

  return {
    claimReward,
    isError,
    error,
    isLoading,
    isSuccess,
  };
};
